/**
 * Supplier Merge confirmation dialog — CR-5901
 *
 * Shows what a merge will do before anything is written: the history each
 * supplier holds (so the admin keeps the one with the history), what happens
 * to every customer relationship and its supplier codes, and only the data
 * areas that actually change.
 */
import type { SelectableSupplier, SupplierMergeHistoryCounts, SupplierMergePreview } from '../../../lib/api';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { AlertTriangle, Loader2 } from 'lucide-react';

interface SupplierMergePreviewDialogProps {
  preview: SupplierMergePreview | null;
  correctSupplier: SelectableSupplier | null;
  incorrectSupplier: SelectableSupplier | null;
  confirming: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

function historyLine(counts: SupplierMergeHistoryCounts): string {
  return [
    plural(counts.orders, 'order'),
    plural(counts.ncrs, 'NCR'),
    plural(counts.sites, 'customer site'),
  ].join(' · ');
}

export default function SupplierMergePreviewDialog({
  preview,
  correctSupplier,
  incorrectSupplier,
  confirming,
  onCancel,
  onConfirm,
}: SupplierMergePreviewDialogProps) {
  const keptName = correctSupplier?.name ?? 'the retained supplier';
  const rows = preview
    ? Object.entries(preview.results).filter(
        ([, r]) => r.moved + r.combined + r.already_on_kept > 0,
      )
    : [];
  const duplicateHasMoreHistory =
    !!preview && preview.sides.incorrect.orders > preview.sides.correct.orders;

  return (
    <Dialog open={preview !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Confirm Supplier Merge
          </DialogTitle>
          <DialogDescription>
            This action is high-impact and cannot be reversed automatically.
            Review what will change before confirming.
          </DialogDescription>
        </DialogHeader>
        {preview && (
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="bg-muted/40 rounded p-3">
                <div className="text-xs uppercase text-muted-foreground mb-1">Retained</div>
                <div className="font-medium">{correctSupplier?.name}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {historyLine(preview.sides.correct)}
                </div>
              </div>
              <div className="bg-muted/40 rounded p-3">
                <div className="text-xs uppercase text-muted-foreground mb-1">
                  Archived after merge
                </div>
                <div className="font-medium">{incorrectSupplier?.name}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {historyLine(preview.sides.incorrect)}
                </div>
              </div>
            </div>

            {duplicateHasMoreHistory && (
              <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {incorrectSupplier?.name} has more orders than {correctSupplier?.name}. The
                  record you keep is normally the one holding the history — check you have
                  picked the right one to keep.
                </span>
              </div>
            )}

            {preview.relationships.length > 0 && (
              <div className="border rounded">
                <div className="px-3 py-2 text-xs uppercase text-muted-foreground border-b bg-muted/30">
                  Customer relationships
                </div>
                {preview.relationships.map((change) => (
                  <div
                    key={`${change.customer_id ?? 'none'}-${change.action}`}
                    className="px-3 py-2 text-sm border-b last:border-0 space-y-1"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{change.customer_name || 'No customer'}</span>
                      <Badge variant={change.action === 'combined' ? 'default' : 'secondary'}>
                        {change.action === 'combined' ? 'Will combine' : 'Will move'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {change.action === 'combined'
                        ? `${keptName} already supplies this site, so the two relationships become one. Blank settings are filled from the duplicate and buyers are added together.`
                        : `${keptName} does not supply this site yet, so the relationship moves across with its settings.`}
                    </p>
                    {change.codes_added.length > 0 && (
                      <p className="text-xs">
                        Supplier codes added: <span className="font-medium">{change.codes_added.join(', ')}</span>
                      </p>
                    )}
                    {change.codes_already.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Already on {keptName}: {change.codes_already.join(', ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="border rounded">
              <div className="grid grid-cols-[1fr_5.5rem_5.5rem_7rem] gap-2 px-3 py-2 text-xs uppercase text-muted-foreground border-b bg-muted/30">
                <span>Data area</span>
                <span className="text-right">Will move</span>
                <span className="text-right">Will combine</span>
                <span className="text-right">Already on {keptName}</span>
              </div>
              {rows.length === 0 ? (
                <p className="px-3 py-4 text-sm text-muted-foreground text-center">
                  {incorrectSupplier?.name} holds no data. It will only be archived.
                </p>
              ) : (
                rows.map(([label, r]) => (
                  <div
                    key={label}
                    className="grid grid-cols-[1fr_5.5rem_5.5rem_7rem] gap-2 px-3 py-1.5 text-sm border-b last:border-0"
                  >
                    <span>{label}</span>
                    <span className="text-right tabular-nums">{r.moved}</span>
                    <span className="text-right tabular-nums">{r.combined}</span>
                    <span className="text-right tabular-nums text-muted-foreground">
                      {r.already_on_kept}
                    </span>
                  </div>
                ))
              )}
              <div className="grid grid-cols-[1fr_5.5rem_5.5rem_7rem] gap-2 px-3 py-2 text-sm font-medium bg-muted/20 border-t">
                <span>Totals</span>
                <span className="text-right tabular-nums">{preview.totals.moved}</span>
                <span className="text-right tabular-nums">{preview.totals.combined}</span>
                <span className="text-right tabular-nums text-muted-foreground">
                  {preview.totals.already_on_kept}
                </span>
              </div>
            </div>
            {preview.totals.already_on_kept > 0 && (
              <p className="text-xs text-muted-foreground">
                Rows {keptName} already holds are not copied twice; the duplicate&apos;s copy stays
                with the archived record.
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={confirming}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={confirming}
            className="bg-destructive hover:bg-destructive/90"
          >
            {confirming ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Merging…
              </>
            ) : (
              'Confirm Merge'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
